import { cn, iaRa } from 'lib/helpers/utility';

import PracticeLogo from 'components/shared/PracticeLogo/PracticeLogo';
import { parseHeaderPhoto } from 'components/shared/PracticeLogo/lib/parseHeaderPhoto';

import DocText from '../DocText';
import { DOC_GAPS, DOC_TEXT } from '../lib/docStyles';
import {
  getPracticeAddressLine,
  getPracticeContactLine,
  getPracticeName
} from '../lib/practiceLines';

import ChipLine from './ChipLine';

/**
 * @param {object} params
 * @param {object} params.practice
 * @param {string} params.title - e.g. 'Invoice'.
 * @param {string} [params.kicker] - e.g. 'Statement of charges'.
 * @param {Array<Array<{label?: string, value?: string}>>} [params.lines] - Meta
 *   chip rows under the kicker.
 */
const DocHeader = ({ practice, title, kicker, lines = [], nameClassName, titleClassName }) => {
  const headerPhoto = parseHeaderPhoto(practice?.header_photo);
  const logo = headerPhoto?.jpg || headerPhoto?.png;

  const name = getPracticeName(practice);
  const address = getPracticeAddressLine(practice?.address || practice?.practiceAddress);
  const contact = getPracticeContactLine(practice);

  const idChips = [
    practice?.npi ? { label: `NPI ${practice.npi}` } : null,
    practice?.tax_id ? { label: 'Tax ID: ', value: practice.tax_id } : null
  ].filter(Boolean);

  const docLines = iaRa(lines).filter((line) => iaRa(line).length);

  return (
    <div>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
        {logo && (
          <div className="w-12 shrink-0">
            <PracticeLogo
              className="h-12 w-12 rounded-[10px] object-contain"
              image={logo}
              practiceId={practice?.id}
            />
          </div>
        )}
        <div className="flex min-w-[180px] flex-1 flex-col">
          {name && (
            <DocText size="medium" className={cn(DOC_TEXT.practiceName, nameClassName)}>
              {name}
            </DocText>
          )}
          <div className="mt-1">
            <ChipLine chips={idChips} />
            {address && (
              <DocText size="xs" className={DOC_TEXT.headerBody}>
                {address}
              </DocText>
            )}
            {contact && (
              <DocText size="xs" className={cn(DOC_TEXT.headerBody, 'break-words')}>
                {contact}
              </DocText>
            )}
          </div>
        </div>
        <div className="ml-auto min-w-0 max-w-[280px] text-right">
          {title && (
            <DocText size="large" className={cn(DOC_TEXT.docTitle, 'text-right', titleClassName)}>
              {title}
            </DocText>
          )}
          {kicker && (
            <DocText variant="label" size="small" className={cn(DOC_TEXT.docKicker, 'text-right')}>
              {String(kicker).toUpperCase()}
            </DocText>
          )}
          {!!docLines.length && (
            <div className="mt-1">
              {docLines.map((chips, index) => (
                <ChipLine key={`doc-line-${index}`} chips={chips} className="text-right" />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={cn(DOC_GAPS.headerRule, 'h-0.5 bg-neutral-900')} />
    </div>
  );
};

export default DocHeader;
